import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, Trash2, Link2, Copy, X, Plus, Eye, Pencil, Archive, ArchiveRestore } from 'lucide-react';
import { chProjectService } from '../services';
import { chClientTokenService, type ChClientAccessToken } from '../services/clientService';
import { useChAuth } from '../contexts/ChAuthContext';
import { useModal } from '../../contexts/ModalContext';
import { isChStaffRole, type ChProject } from '../types';

interface Props {
  project: ChProject;
}

export function ChSettingsTab({ project }: Props) {
  const navigate = useNavigate();
  const { user } = useChAuth();
  const { showConfirm, toast } = useModal();
  // Admin and Account manage projects
  const canManage = isChStaffRole(user?.role);

  const [form, setForm] = useState({
    name: project.name,
    slug: project.slug,
    workcode: project.workcode || '',
    tone_profile: project.tone_profile || '',
  });
  const [archived, setArchived] = useState(project.archived);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setSaving(true);

    try {
      await chProjectService.update(project.id, {
        name: form.name,
        slug: form.slug,
        workcode: form.workcode || null,
        tone_profile: form.tone_profile || null,
      });
      setSuccess(true);
      if (form.slug !== project.slug) {
        navigate(`/contenthub/projetos/${form.slug}`, { replace: true });
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao guardar');
    } finally {
      setSaving(false);
    }
  }

  async function handleArchive() {
    const next = !archived;
    try {
      await chProjectService.update(project.id, { archived: next });
      setArchived(next);
      toast.success(next ? 'Projeto arquivado. Continua em Projetos > Arquivados.' : 'Projeto reativado');
    } catch (err: any) {
      toast.error(err.message || 'Não foi possível arquivar o projeto');
    }
  }

  async function handleDelete() {
    const posts = await chProjectService.countPosts(project.id).catch(() => null);
    const lost = posts === null
      ? 'todo o conteúdo, stats, comentários e links'
      : `${posts} ${posts === 1 ? 'conteúdo' : 'conteúdos'} com stats, comentários e links`;
    const confirmed = await showConfirm({
      title: `Eliminar "${project.name}"?`,
      message: `Apaga o projeto e ${lost}. Não dá para desfazer. Para o tirar da lista sem perder nada, arquiva-o.`,
      confirmText: 'Eliminar de vez',
      cancelText: 'Cancelar',
      variant: 'danger',
    });
    if (!confirmed) return;
    try {
      await chProjectService.delete(project.id);
      toast.success('Projeto eliminado');
      navigate('/contenthub/projetos');
    } catch (err: any) {
      setError(err.message || 'Erro ao eliminar');
      toast.error(err.message || 'Erro ao eliminar o projeto');
    }
  }

  return (
    <div className="max-w-2xl ch-animate-in space-y-6">
      <form onSubmit={handleSave} className="ch-card space-y-5">
        {error && (
          <div className="p-3 rounded-xl bg-red-50 text-red-700 text-sm font-medium">{error}</div>
        )}
        {success && (
          <div className="p-3 rounded-xl bg-green-50 text-green-700 text-sm font-medium">
            Projeto atualizado com sucesso.
          </div>
        )}

        <div>
          <label className="ch-label">Nome *</label>
          <input
            type="text"
            className="ch-input"
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            required
            disabled={!canManage}
          />
        </div>

        <div>
          <label className="ch-label">Slug</label>
          <input
            type="text"
            className="ch-input font-mono text-sm"
            value={form.slug}
            onChange={e => setForm(f => ({ ...f, slug: e.target.value }))}
            required
            disabled={!canManage}
          />
        </div>

        <div>
          <label className="ch-label">Código de Campanha</label>
          <input
            type="text"
            className="ch-input"
            value={form.workcode}
            onChange={e => setForm(f => ({ ...f, workcode: e.target.value }))}
            disabled={!canManage}
          />
        </div>

        <div>
          <label className="ch-label">Tom e Voz</label>
          <textarea
            className="ch-textarea"
            value={form.tone_profile}
            onChange={e => setForm(f => ({ ...f, tone_profile: e.target.value }))}
            disabled={!canManage}
            rows={4}
          />
        </div>

        {canManage && (
          <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={handleArchive}
                className="ch-btn ch-btn-sm ch-btn-ghost"
              >
                {archived ? <ArchiveRestore className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
                {archived ? 'Reativar' : 'Arquivar'}
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className="ch-btn ch-btn-sm text-red-600 hover:bg-red-50"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Eliminar
              </button>
            </div>
            <button type="submit" disabled={saving} className="ch-btn ch-btn-primary">
              <Save className="w-4 h-4" />
              {saving ? 'A guardar...' : 'Guardar'}
            </button>
          </div>
        )}
      </form>

      {/* Client links section */}
      {canManage && <ClientLinksSection projectId={project.id} />}
    </div>
  );
}

function ClientLinksSection({ projectId }: { projectId: string }) {
  const { showConfirm, toast } = useModal();
  const [tokens, setTokens] = useState<ChClientAccessToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newMode, setNewMode] = useState<'review' | 'view'>('review');
  const [showForm, setShowForm] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    loadTokens();
  }, [projectId]);

  async function loadTokens() {
    try {
      const data = await chClientTokenService.getByProject(projectId);
      setTokens(data);
    } catch {
      toast.error('Erro ao carregar os links de cliente');
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setCreating(true);
    try {
      const token = await chClientTokenService.create(projectId, newLabel || undefined, undefined, newMode);
      setTokens(prev => [token, ...prev]);
      setNewLabel('');
      setNewMode('review');
      setShowForm(false);
    } catch {
      toast.error('Erro ao criar o link de cliente');
    } finally {
      setCreating(false);
    }
  }

  async function handleRevoke(id: string) {
    const confirmed = await showConfirm({
      title: 'Revogar link',
      message: 'Revogar este link? Os clientes deixarão de ter acesso.',
      confirmText: 'Revogar',
      cancelText: 'Cancelar',
      variant: 'danger',
    });
    if (!confirmed) return;
    try {
      await chClientTokenService.revoke(id);
      setTokens(prev => prev.map(t => t.id === id ? { ...t, revoked: true } : t));
      toast.success('Link revogado');
    } catch {
      toast.error('Erro ao revogar o link');
    }
  }

  function copyLink(token: string) {
    const url = `${window.location.origin}/contenthub/review/${token}`;
    navigator.clipboard.writeText(url);
    setCopied(token);
    setTimeout(() => setCopied(null), 2000);
  }

  const activeTokens = tokens.filter(t => !t.revoked);
  const revokedTokens = tokens.filter(t => t.revoked);

  return (
    <div className="ch-card space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Link2 className="w-4 h-4 text-[var(--ch-ink)]" />
          <h3 className="text-sm font-bold text-[var(--ch-ink)]">Links de cliente</h3>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="ch-btn ch-btn-primary ch-btn-sm"
        >
          <Plus className="w-3.5 h-3.5" />
          Gerar link
        </button>
      </div>

      <p className="text-xs text-gray-400">
        Partilha estes links com clientes para revisão e aprovação de conteúdo sem login.
      </p>

      {showForm && (
        <form onSubmit={handleCreate} className="space-y-3 p-3 rounded-xl bg-gray-50">
          <div>
            <label className="ch-label">Etiqueta (opcional)</label>
            <input
              type="text"
              className="ch-input h-9 text-sm"
              placeholder="Ex: João - Director Criativo"
              value={newLabel}
              onChange={e => setNewLabel(e.target.value)}
            />
          </div>
          <div>
            <label className="ch-label">Modo de acesso</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setNewMode('review')}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border-2 transition-colors ${newMode === 'review' ? 'border-[var(--ch-ink)] bg-[var(--ch-ink)] text-white' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
              >
                <Pencil className="w-3 h-3" />
                Revisão
              </button>
              <button
                type="button"
                onClick={() => setNewMode('view')}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border-2 transition-colors ${newMode === 'view' ? 'border-[var(--ch-ink)] bg-[var(--ch-ink)] text-white' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
              >
                <Eye className="w-3 h-3" />
                Apenas ver
              </button>
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => setShowForm(false)} className="ch-btn ch-btn-ghost ch-btn-sm">
              <X className="w-3.5 h-3.5" />
            </button>
            <button type="submit" disabled={creating} className="ch-btn ch-btn-primary ch-btn-sm">
              {creating ? '...' : 'Criar'}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="animate-pulse space-y-2">
          <div className="h-10 bg-gray-100 rounded-xl" />
          <div className="h-10 bg-gray-100 rounded-xl" />
        </div>
      ) : activeTokens.length === 0 && !showForm ? (
        <p className="text-xs text-gray-400 py-2">Nenhum link ativo.</p>
      ) : (
        <div className="space-y-2">
          {activeTokens.map(t => (
            <div key={t.id} className="flex items-center gap-2 p-3 rounded-xl bg-gray-50">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <p className="text-xs font-semibold text-[var(--ch-ink)] truncate">
                    {t.label || 'Sem etiqueta'}
                  </p>
                  <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${t.mode === 'view' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                    {t.mode === 'view' ? 'Ver' : 'Revisão'}
                  </span>
                </div>
                <p className="text-[10px] text-gray-400 font-mono truncate">
                  /review/{t.token.slice(0, 12)}...
                </p>
              </div>
              <button
                onClick={() => copyLink(t.token)}
                className="p-1.5 rounded-lg hover:bg-gray-200 transition-colors text-gray-500"
                title="Copiar link"
              >
                {copied === t.token ? (
                  <span className="text-[10px] font-bold text-green-600">Copiado!</span>
                ) : (
                  <Copy className="w-3.5 h-3.5" />
                )}
              </button>
              <button
                onClick={() => handleRevoke(t.id)}
                className="p-1.5 rounded-lg hover:bg-red-50 transition-colors text-gray-400 hover:text-red-500"
                title="Revogar"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {revokedTokens.length > 0 && (
        <details className="text-xs">
          <summary className="text-gray-400 cursor-pointer hover:text-gray-600">
            {revokedTokens.length} link(s) revogado(s)
          </summary>
          <div className="mt-2 space-y-1">
            {revokedTokens.map(t => (
              <div key={t.id} className="flex items-center gap-2 p-2 rounded-lg bg-gray-50 opacity-50">
                <span className="text-gray-400 truncate">{t.label || 'Sem etiqueta'}</span>
                <span className="text-[10px] text-red-400 font-semibold ml-auto">Revogado</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
